// Locate a verbatim quote inside its source text for in-context
// highlighting. Uses the SAME normalization the verifier uses
// (lowercase + collapse whitespace — mirrors normalize() in
// workers/api/src/lib/fact-extraction.ts), with an index map back to the
// original string so the highlight lands on the real characters.
// highlight-find matches verify-find: if the fact verified, the exact
// locator succeeds unless formatting boundaries (e.g. mid-quote markup)
// intervene — then the prefix fallback finds the nearest match and the
// caller shows a notice. Never a silent no-op.

export interface QuoteMatch {
  start: number;
  end: number;
  exact: boolean;
}

export function locateQuote(source: string, quote: string): QuoteMatch | null {
  if (!source || !quote) return null;
  // Build the normalized source and a map from each normalized char to its
  // original index.
  let norm = "";
  const map: number[] = [];
  let pendingSpace = false;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i]!;
    if (/\s/.test(ch)) {
      if (norm.length > 0) pendingSpace = true;
      continue;
    }
    if (pendingSpace) {
      norm += " ";
      map.push(i);
      pendingSpace = false;
    }
    norm += ch.toLowerCase();
    map.push(i);
  }
  const nq = quote.toLowerCase().replace(/\s+/g, " ").trim();
  if (nq.length < 3) return null;

  let idx = norm.indexOf(nq);
  if (idx >= 0) {
    return { start: map[idx]!, end: map[idx + nq.length - 1]! + 1, exact: true };
  }
  // Fallback: nearest match on the longest leading word run (>= 12 chars of
  // normalized prefix), so a quote spanning a formatting boundary still
  // lands the reader in the right place.
  const words = nq.split(" ");
  for (let take = Math.min(words.length - 1, 8); take >= 2; take--) {
    const prefix = words.slice(0, take).join(" ");
    if (prefix.length < 12) break;
    idx = norm.indexOf(prefix);
    if (idx >= 0) {
      return { start: map[idx]!, end: map[idx + prefix.length - 1]! + 1, exact: false };
    }
  }
  return null;
}
